import React, { useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewportHeight } from "@/hooks/useIsMobile";
import MobileNavbar from "@/components/mobile/MobileNavbar";
import MobileHero from "@/components/mobile/MobileHero";
import MobileWhy1 from "@/components/mobile/MobileWhy1";
import MobileWhy2 from "@/components/mobile/MobileWhy2";
import MobileComparison from "@/components/mobile/MobileComparison";
import MobileFeatures from "@/components/mobile/MobileFeatures";
import MobilePricing from "@/components/mobile/MobilePricing";
import MobileTestimonials from "@/components/mobile/MobileTestimonials";
import MobileCTA from "@/components/mobile/MobileCTA";
import MobileFooter from "@/components/mobile/MobileFooter";

/**
 * HomePage optimizado específicamente para dispositivos móviles iOS 26
 *
 * Características:
 * - Gestión agresiva de memoria (límite ~50-70MB en iPhone 13)
 * - Viewport height dinámico para Liquid Glass UI
 * - Safe-area-insets para notch y barras flotantes
 * - Lazy loading y limpieza de recursos
 * - Sin dependencias de componentes desktop
 * - Solo usa claves de traducción (i18n)
 */
const MobileHomePage: React.FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Hook personalizado para fijar viewport height en iOS 26
  useViewportHeight();

  const baseUrl = "https://vettconsult.com";
  const currentLang = i18n.language || "en";

  // SEO data desde traducciones
  const seoTitle = t("seo.home.title");
  const seoDescription = t("seo.home.description");
  const seoKeywords = t("seo.home.keywords");
  const seoOgImage = `${baseUrl}${t("seo.home.ogImage")}`;

  // Canonical URL
  const canonicalUrl = `${baseUrl}${location.pathname}`;

  // Alternate URLs para hreflang
  const alternateUrls = {
    en: `${baseUrl}/en`,
    es: `${baseUrl}/es`,
    fr: `${baseUrl}/fr`,
  };

  // Gestión de memoria agresiva para iOS 26
  const cleanupMemory = useCallback(() => {
    // Limpiar referencias globales innecesarias
    const perf = window.performance as any;
    if (perf && perf.memory) {
      const used = perf.memory.usedJSHeapSize;
      const limit = perf.memory.jsHeapSizeLimit;
      const ratio = used / limit;

      // Si estamos usando más del 70% de memoria, limpiar
      if (ratio > 0.7) {
        console.warn("[MobileHomePage] Memoria alta, limpiando...");

        // Forzar garbage collection si está disponible
        if ((window as any).gc) {
          (window as any).gc();
        }
      }
    }
  }, []);

  // Monitorear memoria cada 10 segundos
  useEffect(() => {
    const memoryInterval = setInterval(cleanupMemory, 10000);

    return () => {
      clearInterval(memoryInterval);
      cleanupMemory();
    };
  }, [cleanupMemory]);

  // Limpiar memoria cuando la página pasa a background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        cleanupMemory();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cleanupMemory]);

  // Structured Data - Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "VettConsult",
    url: baseUrl,
    logo: `${baseUrl}/images/logo/LogoVettConsultTransparent.webp`,
    description: seoDescription,
    sameAs: [
      "https://www.facebook.com/vettconsult",
      "https://www.linkedin.com/company/vettconsult",
      "https://twitter.com/vettconsult",
      "https://www.instagram.com/vettconsult",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      email: "support@vettconsult.com",
      availableLanguage: ["English", "Spanish", "French"],
    },
  };

  // Structured Data - WebSite
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "VettConsult",
    url: baseUrl,
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <html lang={currentLang} />
        <title>{seoTitle}</title>
        <meta name="title" content={seoTitle} />
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />

        {/* Viewport meta - CRÍTICO para iOS 26 */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes, viewport-fit=cover"
        />

        {/* iOS specific meta tags */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="apple-mobile-web-app-title" content="VettConsult" />

        {/* Canonical + hreflang are owned by PublicLayout (single source of
            truth) to avoid a conflicting second canonical. */}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoOgImage} />
        <meta property="og:image:width" content="1563" />
        <meta property="og:image:height" content="1563" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoOgImage} />

        {/* SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="theme-color" content="#00e9be" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>

      {/* Mobile Navbar */}
      <MobileNavbar />

      {/* Main Content */}
      <main className="bg-gradient-to-b from-[#ccfbf1] via-[#d1fae5] to-[#ccfbf1]">
        {/* Hero Section */}
        <MobileHero />

        {/* Why Sections */}
        <MobileWhy1 />
        <MobileWhy2 />

        {/* Comparison Section */}
        <MobileComparison />

        {/* Features Section */}
        <MobileFeatures />

        {/* Pricing Section */}
        <MobilePricing />

        {/* Testimonials Section */}
        <MobileTestimonials />

        {/* CTA Section */}
        <MobileCTA />

        {/* Mobile Footer */}
        <MobileFooter />
      </main>
    </>
  );
};

export default MobileHomePage;
